import { useState, useEffect } from "react";
import supabase from "../supabase/supabase";

interface WordCountData {
  enhancedWords: number;
  remainingWords: number;
  isUnlimited: boolean;
  monthlyLimit: number;
}

const WORD_LIMIT = 2000;
const LOCAL_STORAGE_KEY = "writevoice_word_count";

export const useWordCount = () => {
  const [wordCount, setWordCount] = useState<WordCountData>({
    enhancedWords: 0,
    remainingWords: WORD_LIMIT,
    isUnlimited: false,
    monthlyLimit: WORD_LIMIT,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWordCount();
  }, []);

  const loadWordCount = async () => {
    try {
      // Check if user is authenticated
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // Load from backend for authenticated users
        const response = await fetch(
          `${
            process.env.REACT_APP_EXPRESS_BACKEND_URL || "https://api.writevoice.io"
          }/api/usage/credits`,
          {
            headers: {
              Authorization: `Bearer ${
                (
                  await supabase.auth.getSession()
                ).data.session?.access_token
              }`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setWordCount({
            enhancedWords: data.usedWords || 0,
            remainingWords: data.remainingWords || 0,
            isUnlimited: data.isUnlimited || false,
            monthlyLimit: data.monthlyLimit || WORD_LIMIT,
          });
        } else {
          // Fallback to local storage
          loadLocalWordCount();
        }
      } else {
        // Load from local storage for offline users
        loadLocalWordCount();
      }
    } catch (error) {
      console.error("Error loading word count:", error);
      loadLocalWordCount();
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocalWordCount = () => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        
        // Check if 30 days have passed since the cycle started
        const cycleStartDate = data.cycleStartDate ? new Date(data.cycleStartDate) : new Date();
        const now = new Date();
        const daysSinceStart = Math.floor((now.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysSinceStart >= 30) {
          // 30 days have passed, reset count and start new cycle
          const resetData = {
            enhancedWords: 0,
            remainingWords: WORD_LIMIT,
            isUnlimited: false,
            monthlyLimit: WORD_LIMIT,
            cycleStartDate: now.toISOString(),
          };
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resetData));
          setWordCount(resetData);
        } else {
          setWordCount({
            enhancedWords: data.enhancedWords || 0,
            remainingWords: data.remainingWords || WORD_LIMIT,
            isUnlimited: data.isUnlimited || false,
            monthlyLimit: data.monthlyLimit || WORD_LIMIT,
          });
        }
      } catch (error) {
        console.error("Error parsing local word count:", error);
        resetLocalWordCount();
      }
    } else {
      resetLocalWordCount();
    }
  };

  const resetLocalWordCount = () => {
    const resetData = {
      enhancedWords: 0,
      remainingWords: WORD_LIMIT,
      isUnlimited: false,
      monthlyLimit: WORD_LIMIT,
      cycleStartDate: new Date().toISOString(),
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(resetData));
    setWordCount(resetData);
  };

  const incrementWordCount = async (words: number) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // Update backend for authenticated users
        const response = await fetch(
          `${
            process.env.REACT_APP_EXPRESS_BACKEND_URL || "https://api.writevoice.io"
          }/api/usage/increment`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${
                (
                  await supabase.auth.getSession()
                ).data.session?.access_token
              }`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ words }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setWordCount({
            enhancedWords: data.usedWords || 0,
            remainingWords: data.remainingWords || 0,
            isUnlimited: data.isUnlimited || false,
            monthlyLimit: data.monthlyLimit || WORD_LIMIT,
          });
        }
      } else {
        // Update local storage for offline users
        const newEnhancedWords = wordCount.enhancedWords + words;
        const newRemainingWords = Math.max(0, WORD_LIMIT - newEnhancedWords);

        // Preserve the cycle start date when updating
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        const existingData = stored ? JSON.parse(stored) : {};
        
        const updatedData = {
          enhancedWords: newEnhancedWords,
          remainingWords: newRemainingWords,
          isUnlimited: false,
          monthlyLimit: WORD_LIMIT,
          cycleStartDate: existingData.cycleStartDate || new Date().toISOString(),
        };

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedData));
        setWordCount(updatedData);
      }
    } catch (error) {
      console.error("Error incrementing word count:", error);
    }
  };

  const hasCredits = () => {
    return wordCount.isUnlimited || wordCount.remainingWords > 0;
  };

  return {
    wordCount,
    isLoading,
    incrementWordCount,
    hasCredits,
    refreshWordCount: loadWordCount,
  };
};
